/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useI18n } from '@/context/I18nContext';
import { formatUnixTimestampMs } from '@/lib/formatting/dateTime';
import type { ParsedIntervalValueCsv } from '@/lib/timeSeries/parseIntervalValueCsv';

interface IntervalValuePreviewProps {
    preview: ParsedIntervalValueCsv;
}

function IntervalValuePreview({ preview }: IntervalValuePreviewProps) {
    const { locale, t } = useI18n();

    return (
        <div className="w-full overflow-x-auto rounded-box border border-base-content/20">
            <table className="table table-sm w-full">
                <thead className="bg-base-content/10 text-sm text-base-content/80">
                    <tr>
                        <th className="px-4 py-3" scope="col">{t('Date')}</th>
                        {preview.series.map((series) => (
                            <th key={series.column} className="px-4 py-3" scope="col">
                                <span className="block">{series.urn}</span>
                                {series.unit ? (
                                    <span className="block text-xs font-normal text-base-content/70">
                                        [{series.unit}]
                                    </span>
                                ) : null}
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody className="divide-y divide-base-300">
                    {preview.rows.map((row, index) => {
                        const timestampSeconds = Number.parseInt(row.timestamp, 10);
                        const timestamp = Number.isNaN(timestampSeconds)
                            ? Number.NaN
                            : timestampSeconds * 1000;

                        return (
                            <tr key={`${row.timestamp}-${index}`} className="even:bg-base-content/5 hover:bg-base-content/10">
                                <td className="align-top px-4 py-3 font-mono text-xs text-base-content/80 md:text-sm">
                                    {Number.isNaN(timestamp)
                                        ? '-'
                                        : formatUnixTimestampMs(String(timestamp), locale, '-')}
                                </td>
                                {preview.series.map((series) => (
                                    <td key={series.column} className="align-top px-4 py-3 font-mono text-xs text-base-content/80 md:text-sm">
                                        {row.values[series.urn] || '-'}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default IntervalValuePreview;
